"use client";

import React, { useRef, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ArrowLeft } from "lucide-react";
import { gsap } from "gsap";
import clsx from "clsx";
import { navigateToSelectedWork, navigateToAboutSection, scrollToSection } from "@/utils/navigation";
import { useAnalytics } from "@/hooks/useAnalytics";
import { RESUME_URL } from "@/lib/site";

interface NavigationItem {
  label: string;
  href: string;
  action?: "scroll" | "external" | "pdf";
  target?: string;
}

const navigationItems: NavigationItem[] = [
  { label: "Work", href: "#", action: "scroll", target: "selected-work" },
  {
    label: "About",
    href: "#",
    action: "scroll",
    target: "amilcar-teixeira-section",
  },
  {
    label: "Contact",
    href: "#",
    action: "scroll",
    target: "footer-section",
  },
  { label: "Notes", href: "/notes" },
  { label: "Resume", href: RESUME_URL, action: "pdf" },
];

const NAV_TEXT = "text-[#111111]";
const NAV_HOVER = "hover:text-[#555555]";

const TIME_ZONE_LABELS: Record<string, string> = {
  "America/Sao_Paulo": "RJ",
  "Europe/London": "GB",
  "America/New_York": "NY",
};

const getTimeZoneLabel = (timeZone: string, date: Date) => {
  const knownLabel = TIME_ZONE_LABELS[timeZone];
  if (knownLabel) return knownLabel;

  const timeZoneName = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;

  return timeZoneName ?? "LOCAL";
};

const Navigation: React.FC = () => {
  const navRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>("");
  const [locationLabel, setLocationLabel] = useState<string>("");
  const { trackNavigation, trackContact } = useAnalytics();

  // Live clock based on the visitor's browser timezone
  useEffect(() => {
    const timeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    const updateTime = () => {
      const now = new Date();
      const localTime = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(now);

      setCurrentTime(localTime);
      setLocationLabel(getTimeZoneLabel(timeZone, now));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".nav-item",
        {
          y: -20,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
        }
      );
    }, navRef);

    return () => ctx.revert();
  }, []);

  const handleNavigationClick = (item: NavigationItem, e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);

    // Track the navigation click
    trackNavigation('click', item.label, item.href, pathname);

    switch (item.action) {
      case "scroll":
        if (item.target === "selected-work") {
          // Always navigate to home page first, then scroll to selected work
          if (pathname !== "/") {
            navigateToSelectedWork(router);
          } else {
            scrollToSection("selected-work-section");
          }
        } else if (item.target === "amilcar-teixeira-section") {
          // Always navigate to home page first, then scroll to about section
          if (pathname !== "/") {
            navigateToAboutSection(router);
          } else {
            scrollToSection("amilcar-teixeira-section");
          }
        } else if (item.target === "footer-section") {
          // Always navigate to home page first, then scroll to footer
          if (pathname !== "/") {
            router.push("/");
            setTimeout(() => {
              scrollToSection("footer-section");
            }, 100);
          } else {
            scrollToSection("footer-section");
          }
        }
        break;
      case "external":
        window.open(item.href, "_blank", "noopener,noreferrer");
        break;
      case "pdf":
        // Track resume download
        trackContact('resume_download', 'Resume PDF', {
          linkType: 'resume',
          downloadType: 'pdf',
        });
        window.open(item.href, "_blank");
        break;
      default:
        router.push(item.href);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
    }
  };

  const textColorClass = NAV_TEXT;
  const hoverColorClass = NAV_HOVER;

  return (
    <nav
      ref={navRef}
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: "rgba(250, 252, 253, 0.92)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        willChange: "transform",
      }}
      aria-label="Main navigation"
    >
      {/* Backdrop blur */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: "blur(8px) saturate(140%)",
          WebkitBackdropFilter: "blur(8px) saturate(140%)",
        }}
      />
      <div
        className="px-6 md:px-12 lg:px-20 py-3 relative z-10"
        style={{
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        <div className="flex items-center justify-between h-10">
          {/* Logo / Back to Home */}
          <Link
            href="/"
            className={clsx(
              "nav-item text-[24px] font-bold transition-colors duration-300 font-pp-neuebit inline-flex items-center gap-2 hover:opacity-75",
              textColorClass,
              hoverColorClass
            )}
            onClick={(e) => {
              // Track logo/home click
              const isBackNavigation = pathname.startsWith('/projects/') && pathname !== '/projects';
              if (isBackNavigation) {
                trackNavigation('back_navigation', 'Back to Home', '/', pathname);
                e.preventDefault();
                router.push('/');
              } else {
                trackNavigation('logo_click', 'Logo Home', '/', pathname);
              }
            }}
          >
            {pathname.startsWith('/projects/') && pathname !== '/projects' ? (
              <>
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </>
            ) : (
              'Amilcar Teixeira'
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Live Clock - First item */}
            <div
              className={clsx(
                "nav-item text-[24px] font-pp-neuebit",
                textColorClass
              )}
            >
              {currentTime && locationLabel
                ? `${currentTime} ${locationLabel}`
                : null}
            </div>

            {navigationItems.map((item) => {
              if (
                item.action === "scroll" ||
                item.action === "external" ||
                item.action === "pdf"
              ) {
                return (
                  <button
                    key={item.label}
                    onClick={(e) => handleNavigationClick(item, e)}
                    className={clsx(
                      "nav-item relative px-2 py-1 text-[24px] font-pp-neuebit transition-[color,opacity] duration-300 opacity-70 hover:opacity-100",
                      textColorClass,
                      hoverColorClass,
                      "focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
                    )}
                    onKeyDown={handleKeyDown}
                  >
                    {item.label}
                  </button>
                );
              } else {
                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={() =>
                      trackNavigation('click', item.label, item.href, pathname)
                    }
                    className={clsx(
                      "nav-item relative px-2 py-1 text-[24px] font-pp-neuebit transition-[color,opacity] duration-300 opacity-70 hover:opacity-100",
                      textColorClass,
                      hoverColorClass,
                      "focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
                    )}
                    onKeyDown={handleKeyDown}
                  >
                    {item.label}
                  </Link>
                );
              }
            })}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={clsx(
              "md:hidden nav-item p-2 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2",
              textColorClass,
              hoverColorClass
            )}
            onClick={() => {
              const newMenuState = !isMenuOpen;
              setIsMenuOpen(newMenuState);
              // Track menu toggle
              trackNavigation('menu_toggle', `Menu ${newMenuState ? 'Open' : 'Close'}`, undefined, pathname);
            }}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label="Toggle navigation menu"
          >
            {/* All three bars sit at the same `top` and move with transforms —
                animating `top` would lay out the bar on every frame. */}
            <div className="w-6 h-6 relative">
              <span
                className={clsx(
                  "absolute top-2.5 w-full h-0.5 bg-current transition-transform duration-300",
                  isMenuOpen ? "translate-y-0 rotate-45" : "-translate-y-1.5"
                )}
              />
              <span
                className={clsx(
                  "absolute top-2.5 w-full h-0.5 bg-current transition-opacity duration-300",
                  isMenuOpen ? "opacity-0" : "opacity-100"
                )}
              />
              <span
                className={clsx(
                  "absolute top-2.5 w-full h-0.5 bg-current transition-transform duration-300",
                  isMenuOpen ? "translate-y-0 -rotate-45" : "translate-y-1.5"
                )}
              />
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className={clsx(
            // Absolutely positioned so opening the menu never resizes the bar,
            // and animated on transform/opacity only — max-height would force a
            // reflow on every frame of the transition.
            "md:hidden absolute left-0 right-0 top-full origin-top",
            isMenuOpen
              ? "opacity-100 translate-y-0 visible"
              : "opacity-0 -translate-y-2 invisible pointer-events-none"
          )}
          style={{
            // Fully opaque: the panel has to isolate itself from the page it
            // sits over, so nothing bleeds through during the transition.
            background: "#fafcfd",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 12px 24px -16px rgba(0,0,0,0.25)",
            transition:
              "opacity 300ms ease-out, transform 300ms ease-out, visibility 300ms",
          }}
          role="menu"
          aria-labelledby="mobile-menu-button"
        >
          <div className="px-4 pt-2 pb-3 space-y-1 mt-2">
            {navigationItems.map((item) => {
              if (
                item.action === "scroll" ||
                item.action === "external" ||
                item.action === "pdf"
              ) {
                return (
                  <button
                    key={item.label}
                    onClick={(e) => handleNavigationClick(item, e)}
                    className={clsx(
                      "block px-3 py-2 text-base font-medium rounded-md transition-[color,opacity] duration-300 w-full text-left opacity-70 hover:opacity-100",
                      textColorClass,
                      hoverColorClass,
                      "focus:outline-none focus:ring-2 focus:ring-white focus:ring-inset"
                    )}
                    onKeyDown={handleKeyDown}
                  >
                    {item.label}
                  </button>
                );
              } else {
                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={() => {
                      trackNavigation('click', item.label, item.href, pathname);
                      setIsMenuOpen(false);
                    }}
                    className={clsx(
                      "block px-3 py-2 text-base font-medium rounded-md transition-[color,opacity] duration-300 opacity-70 hover:opacity-100",
                      textColorClass,
                      hoverColorClass,
                      "focus:outline-none focus:ring-2 focus:ring-white focus:ring-inset"
                    )}
                    onKeyDown={handleKeyDown}
                  >
                    {item.label}
                  </Link>
                );
              }
            })}

            {/* Mobile Clock */}
            <div
              className={clsx("px-3 py-2 text-base font-pp-neuebit", textColorClass)}
            >
              <span className="opacity-70">NY Time:</span>
              <span className="ml-2 font-medium">{currentTime}</span>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
